// MeetingManager para CRUD offline-first de metadata

import { v4 as uuid } from 'uuid';
import type { Meeting, Participant, Topic, Block } from '@meeting-copilot/shared';
import { Outbox, OutboxState, calculateSortKey } from './outbox.js';

export class MeetingManager {
  private db: IDBDatabase;
  private outbox: Outbox;

  constructor(db: IDBDatabase, outbox: Outbox) {
    this.db = db;
    this.outbox = outbox;
  }

  // CRÍTICO: Guardar reunión offline-first con UUID client-side
  async saveMeeting(meeting: Meeting): Promise<void> {
    if (!meeting.id) {
      meeting.id = uuid();
    }
    
    meeting.updated_at = new Date().toISOString();
    
    // 1. Guardar local
    const tx = this.db.transaction('meetings', 'readwrite');
    await tx.objectStore('meetings').put(meeting);
    
    // 2. Encolar para sync
    const sort_key = calculateSortKey({
      type: 'metadata',
      data: { timestamp: Date.now() },
    });
    
    await this.outbox.enqueue({
      id: `meeting-${meeting.id}-${Date.now()}`,
      meeting_id: meeting.id,
      type: 'metadata',
      sort_key,
      data: {
        action: 'upsert_meeting',
        meeting,
      },
      state: OutboxState.PENDING,
      retries: 0,
    });
    
    // 3. Sync inmediato si online y syncNow (será manejado por SyncManager)
    console.log('Meeting saved locally:', meeting.id);
  }

  async saveParticipants(meetingId: string, participants: Participant[]): Promise<void> {
    // Asignar IDs si no tienen
    for (const p of participants) {
      if (!p.id) p.id = uuid();
      p.meeting_id = meetingId;
    }
    
    const tx = this.db.transaction('participants', 'readwrite');
    const store = tx.objectStore('participants');
    
    for (const p of participants) {
      await store.put(p);
    }
    
    const sort_key = calculateSortKey({
      type: 'metadata',
      data: { timestamp: Date.now() },
    });
    
    await this.outbox.enqueue({
      id: `participants-${meetingId}-${Date.now()}`,
      meeting_id: meetingId,
      type: 'metadata',
      sort_key,
      data: {
        action: 'upsert_participants',
        meeting_id: meetingId,
        participants,
      },
      state: OutboxState.PENDING,
      retries: 0,
    });
  }

  async saveTopics(meetingId: string, topics: Topic[]): Promise<void> {
    for (const t of topics) {
      if (!t.id) t.id = uuid();
      t.meeting_id = meetingId;
    }
    
    const tx = this.db.transaction('topics', 'readwrite');
    const store = tx.objectStore('topics');
    
    for (const t of topics) {
      await store.put(t);
    }
    
    const sort_key = calculateSortKey({
      type: 'metadata',
      data: { timestamp: Date.now() },
    });
    
    await this.outbox.enqueue({
      id: `topics-${meetingId}-${Date.now()}`,
      meeting_id: meetingId,
      type: 'metadata',
      sort_key,
      data: {
        action: 'upsert_topics',
        meeting_id: meetingId,
        topics,
      },
      state: OutboxState.PENDING,
      retries: 0,
    });
  }

  async getMeeting(meetingId: string): Promise<Meeting | null> {
    const tx = this.db.transaction('meetings', 'readonly');
    const request = tx.objectStore('meetings').get(meetingId);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getParticipants(meetingId: string): Promise<Participant[]> {
    const tx = this.db.transaction('participants', 'readonly');
    const index = tx.objectStore('participants').index('by_meeting');
    const range = IDBKeyRange.only(meetingId);
    
    return new Promise((resolve, reject) => {
      const items: Participant[] = [];
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async getTopics(meetingId: string): Promise<Topic[]> {
    const tx = this.db.transaction('topics', 'readonly');
    const index = tx.objectStore('topics').index('by_meeting_seq');
    const range = IDBKeyRange.bound([meetingId, -Infinity], [meetingId, Infinity]);
    
    return new Promise((resolve, reject) => {
      const items: Topic[] = [];
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items.sort((a, b) => a.seq - b.seq));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async getBlocks(meetingId: string): Promise<Block[]> {
    const tx = this.db.transaction('blocks', 'readonly');
    const index = tx.objectStore('blocks').index('by_meeting_seq');
    const range = IDBKeyRange.bound([meetingId, -Infinity], [meetingId, Infinity]);
    
    return new Promise((resolve, reject) => {
      const items: Block[] = [];
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items.sort((a, b) => a.seq - b.seq));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async updateBlock(block: Block): Promise<void> {
    const tx = this.db.transaction('blocks', 'readwrite');
    await tx.objectStore('blocks').put(block);
  }

  async listMeetings(): Promise<string[]> {
    const tx = this.db.transaction('meetings', 'readonly');
    const store = tx.objectStore('meetings');
    
    return new Promise((resolve, reject) => {
      const request = store.getAllKeys();
      
      request.onsuccess = () => {
        resolve(request.result as string[]);
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async getMeetingsByStatus(status: string): Promise<Meeting[]> {
    const tx = this.db.transaction('meetings', 'readonly');
    const index = tx.objectStore('meetings').index('by_status');
    const range = IDBKeyRange.only(status);
    
    return new Promise((resolve, reject) => {
      const items: Meeting[] = [];
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async getAllMeetings(): Promise<Meeting[]> {
    const tx = this.db.transaction('meetings', 'readonly');
    const store = tx.objectStore('meetings');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  // Save event locally and enqueue for sync
  async saveEvent(meetingId: string, event: any): Promise<void> {
    if (!event.id) {
      event.id = uuid();
    }
    event.meeting_id = meetingId;
    
    // Save to local events store
    const tx = this.db.transaction('events', 'readwrite');
    await tx.objectStore('events').put(event);
    
    // Enqueue for sync
    const sort_key = calculateSortKey({
      type: 'event',
      data: event,
    });
    
    await this.outbox.enqueue({
      id: event.id,
      meeting_id: meetingId,
      type: 'event',
      sort_key,
      data: event,
      state: OutboxState.PENDING,
      retries: 0,
    });
  }

  async getEvents(meetingId: string): Promise<any[]> {
    const tx = this.db.transaction('events', 'readonly');
    const index = tx.objectStore('events').index('by_meeting_time');
    const range = IDBKeyRange.bound([meetingId, -Infinity], [meetingId, Infinity]);
    
    return new Promise((resolve, reject) => {
      const items: any[] = [];
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async getTranscripts(meetingId: string): Promise<any[]> {
    const tx = this.db.transaction('transcripts', 'readonly');
    const index = tx.objectStore('transcripts').index('by_meeting_time');
    const range = IDBKeyRange.bound([meetingId, -Infinity], [meetingId, Infinity]);
    
    return new Promise((resolve, reject) => {
      const items: any[] = [];
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }
}

